import React, { Component } from 'react';
import T from 'prop-types';
import cx from 'classnames';
import { isEmpty } from 'lodash-es';
import DayPicker, { DateUtils } from 'react-day-picker';

import Weekday from './Weekday';
import YearMonthForm from './YearMonthForm';
import CustomDateInput from './CustomDateInput';
import PredefinedRangesDropdown from './PredefinedRangesDropdown';
import Button from '../../Buttons/Button';

import { initialFrom, initialTo, initialToMonth, getFromMonth, getRangeMonths, maskedValue } from '../configs/date-range-picker';
import { dateStringToDate } from '../configs';
import { validateOnBlur, isFormValid } from '../utils/validation';
import {
  format, yesterday, differenceInDays,
  isAfter, isBefore, isValid, addDays, subDays,
} from '../../../utils/date';

class DateRangePicker extends Component {
  static propTypes = {
    maxPeriod: T.number.isRequired,
    currentPeriod: T.number.isRequired,
    maxShowedPeriod: T.number,
    maxSelectablePeriod: T.number,
    minSelectablePeriod: T.number,
    maxDate: T.object,
    dateFrom: T.string,
    dateTo: T.string,
    onSubmit: T.func.isRequired,
    onCancel: T.func.isRequired,
    dataStartDate: T.string,
    limitedPeriodText: T.string,
  };
  constructor(props) {
    super(props);

    const initialFromDate = initialFrom(props.dateFrom, props.currentPeriod, props.maxDate);
    const initialToDate = initialTo(props.dateTo, props.maxDate);

    this.state = {
      from: initialFromDate,
      to: initialToDate,
      inputFrom: format(initialFromDate),
      inputTo: format(initialToDate),
      monthFrom: getFromMonth(initialFromDate, initialToDate, props.maxPeriod),
      monthTo: initialToMonth(initialFromDate, initialToDate, props.maxPeriod),
      errorMessages: {},
      enableApply: false,
    };
  }

  get maxShowedPeriod() {
    return this.props.maxShowedPeriod || differenceInDays(yesterday(), HISTORICAL_START_DATE) + 1;
  }

  get maxSelectablePeriod() {
    const { maxPeriod, maxSelectablePeriod } = this.props;

    if (maxSelectablePeriod && maxSelectablePeriod <= maxPeriod) return maxSelectablePeriod;
    return maxPeriod;
  }

  get minDate() {
    const { maxPeriod, maxDate } = this.props;
    return dateStringToDate(subDays(maxDate, maxPeriod - 1));
  }

  get minPeriodRange() {
    const { maxDate, minSelectablePeriod } = this.props;
    if (minSelectablePeriod) {
      const { from } = this.state;
      let realFrom = from;
      let dateTo = dateStringToDate(addDays(from, minSelectablePeriod - 1));
      if (isAfter(dateTo, maxDate)) {
        dateTo = maxDate;
        realFrom = dateStringToDate(subDays(maxDate, minSelectablePeriod - 1));
      }
      return ({
        'highlighted-min-period': {
          from: realFrom,
          to: dateTo,
        },
        'first-min-period-day': realFrom,
        'last-min-period-day': dateTo,
      });
    }
    return {};
  }

  handleSubmit = (e) => {
    e.preventDefault();
    const { to, from, enableApply } = this.state;
    if (!enableApply) return;

    this.props.onSubmit({
      period: differenceInDays(to, from) + 1,
      date_to: format(to),
      date_from: format(from),
    });
  };

  handleRangeSubmit = ({ period, date_from, date_to }) => {
    const changes = {
      period,
      date_from,
      date_to,
    };

    if (isEmpty(date_to)) {
      changes.date_to = format(this.props.maxDate);
    }

    this.props.onSubmit(changes);
  };

  handleBlur = (value, type) => {
    const { maxDate, minSelectablePeriod } = this.props;
    const from = type === 'from' ? value : this.state.from;
    const to = type === 'to' ? value : this.state.to;

    this.setState({
      errorMessages: validateOnBlur(from, to, {
        type,
        maxDate,
        minDate: this.minDate,
        maxSelectablePeriod: this.maxSelectablePeriod,
        minSelectablePeriod,
      }),
      enableApply: isFormValid(from, to, maxDate, this.maxSelectablePeriod, minSelectablePeriod),
    });
  };

  handleDayClick = (day, modifiers = {}, type) => {
    if (modifiers.disabled) return;

    const { maxDate, maxPeriod } = this.props;
    const { from, to } = this.state;
    const range = DateUtils.addDayToRange(day, { from, to });

    this.setState({
      from: range.from,
      to: range.to,
      inputFrom: range.from && format(range.from),
      inputTo: range.to && format(range.to),
      ...getRangeMonths(range, this.state, maxPeriod, type),
      errorMessages: {},
      enableApply: isFormValid(range.from, range.to, maxDate, this.maxSelectablePeriod),
    }, () => this.handleBlur(range[type], type));
  };

  handleInputChange = (value, type) => {
    const regexp = /^[0-9/]+$/;
    if (value.length > 10 || !regexp.test(value)) return;

    const dateStr = maskedValue(value);
    const inputFieldKey = { from: 'inputFrom', to: 'inputTo' }[type];
    this.setState({
      [inputFieldKey]: dateStr,
      errorMessages: {},
    });

    if (value.length === 10) {
      const nextDate = dateStringToDate(dateStr);

      if (isValid(nextDate)) {
        const { from, to } = this.state;
        if (type === 'from' && nextDate !== from) {
          this.setState({
            from: nextDate,
            ...getRangeMonths({ from: nextDate, to }, this.state, this.props.maxPeriod, type),
          });
        } else if (type === 'to' && nextDate !== to) {
          this.setState({
            to: nextDate,
            ...getRangeMonths({ from, to: nextDate }, this.state, this.props.maxPeriod, type),
          });
        }
      }

      this.handleBlur(nextDate, type);
    }
  };

  render() {
    const {
      from,
      to,
      monthFrom,
      monthTo,
      inputFrom,
      inputTo,
      errorMessages,
      enableApply,
    } = this.state;
    const {
      onCancel,
      maxPeriod,
      maxDate,
      currentPeriod,
      minSelectablePeriod,
      dataStartDate,
      limitedPeriodText,
    } = this.props;
    const disabledDates = [{ after: maxDate }, { before: this.minDate }];
    const modifiers = {
      start: from,
      end: to,
      ...this.minPeriodRange,
    };
    return (
      <form className="date-range-menu__form d-flex flex-column" onSubmit={this.handleSubmit}>
        <header className="date-range-menu__form-header d-flex flex-column">
          <div className="d-flex date-range-menu__form-header-selectors">
            <div className="predefined-periods-box">
              <PredefinedRangesDropdown
                maxPeriod={maxPeriod}
                customTo={to && format(to)}
                customFrom={from && format(from)}
                currentPeriod={currentPeriod}
                maxShowedPeriod={this.maxShowedPeriod}
                minSelectablePeriod={minSelectablePeriod}
                disabled={isBefore(maxDate, yesterday())}
                dataStartDate={dataStartDate}
                limitedPeriodText={limitedPeriodText}
                onChange={this.handleRangeSubmit}
              />
            </div>
            <div className="d-flex inputs-box">
              <CustomDateInput
                value={inputFrom}
                focusOnMount
                className={cx(errorMessages.from && 'error')}
                onChange={v => this.handleInputChange(v, 'from')}
              />
              <div className="divider" />
              <CustomDateInput
                value={inputTo}
                className={cx(errorMessages.to && 'error')}
                onChange={v => this.handleInputChange(v, 'to')}
              />
            </div>
          </div>
          <div className="validation-box">
            {errorMessages.period && errorMessages.period.length > 0 && (
              <div className="period">{errorMessages.period}</div>
            )}
            {errorMessages.from && <div className="from"> {errorMessages.from}</div>}
            {errorMessages.to && <div className="to"> {errorMessages.to}</div>}
            {errorMessages.minPeriodError && <div>{errorMessages.minPeriodError}</div>}
            {errorMessages.multiplePeriodError && <div>{errorMessages.multiplePeriodError}</div>}
          </div>
        </header>
        <div className="d-flex date-range-menu__form-body">
          <div className="pos-rel">
            <DayPicker
              month={monthFrom}
              fromMonth={this.minDate}
              toMonth={maxDate}
              className={cx('Selectable', { 'error-period': errorMessages.minPeriodError || errorMessages.multiplePeriodError })}
              numberOfMonths={1}
              firstDayOfWeek={1}
              selectedDays={[from, { from, to }]}
              modifiers={modifiers}
              disabledDays={disabledDates}
              weekdayElement={<Weekday />}
              captionElement={({ date, localeUtils }) => (
                <YearMonthForm
                  date={date}
                  maxDate={maxDate}
                  localeUtils={localeUtils}
                  maxPeriod={maxPeriod}
                  onChange={dateValue => this.setState({ monthFrom: dateValue })}
                />
              )}
              onMonthChange={month => this.setState({ monthFrom: month })}
              onDayClick={(day, modifiersOption) => this.handleDayClick(day, modifiersOption, 'from')}
            />
          </div>
          <div className="divider-vertical" />
          <div className="pos-rel">
            <DayPicker
              className={cx('Selectable', { 'error-period': errorMessages.minPeriodError || errorMessages.multiplePeriodError })}
              numberOfMonths={1}
              firstDayOfWeek={1}
              month={monthTo}
              fromMonth={this.minDate}
              toMonth={maxDate}
              selectedDays={[from, { from, to }]}
              modifiers={modifiers}
              disabledDays={disabledDates}
              weekdayElement={<Weekday />}
              captionElement={({ date, localeUtils }) => (
                <YearMonthForm
                  date={date}
                  maxDate={maxDate}
                  localeUtils={localeUtils}
                  maxPeriod={maxPeriod}
                  onChange={dateValue => this.setState({ monthTo: dateValue })}
                />
              )}
              onMonthChange={month => this.setState({ monthTo: month })}
              onDayClick={(day, modifiersOption) => this.handleDayClick(day, modifiersOption, 'to')}
            />
          </div>
        </div>
        <footer className="date-range-menu__form-footer d-flex align-items-center justify-content-between">
          <Button
            variant="secondary"
            onClick={onCancel}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={!enableApply}
          >
            Apply
          </Button>
        </footer>
      </form>
    );
  }
}

export default DateRangePicker;
